import React, { useState } from "react";
import { HelpCircle, Send, CheckCircle2 } from "lucide-react";
import Layout from "../components/Layout";
import { apiRequest } from "../api";

export default function Support() {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const faqs = [
    {
      q: "How do I activate a VIP level?",
      a: "Select a VIP level and activate it after completing the required deposit.",
    },
    {
      q: "How often are earnings credited?",
      a: "Daily earnings can be claimed once per day when you have an active VIP level.",
    },
    {
      q: "How do referral bonuses work?",
      a: "You receive referral rewards when users join through your referral link according to the referral rules.",
    },
    {
      q: "What is the minimum withdrawal?",
      a: "The minimum withdrawal amount is 100 USDT.",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    const cleanMessage = message.trim();

    // Validation
    if (!cleanMessage) {
      setError("Please enter your message.");
      return;
    }

    if (cleanMessage.length < 10) {
      setError("Message must be at least 10 characters.");
      return;
    }

    if (cleanMessage.length > 2000) {
      setError("Message cannot exceed 2000 characters.");
      return;
    }

    setLoading(true);

    try {
      const result = await apiRequest("/support", {
        method: "POST",
        body: JSON.stringify({
          message: cleanMessage,
        }),
      });

      setSuccess(result.message || "Your message has been sent successfully.");
      setMessage("");
    } catch (err) {
      setError(err.message || "Unable to send your message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Support">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* FAQ */}
        <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-6">
          <div className="flex items-center gap-2 mb-5">
            <HelpCircle size={18} className="text-[#5B8DEF]" />

            <h2 className="font-semibold text-[15px]">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-5">
            {faqs.map((f, i) => (
              <div
                key={i}
                className="border-b border-[#1B2033] pb-4 last:border-b-0 last:pb-0"
              >
                <div className="text-sm font-medium mb-1">
                  {f.q}
                </div>

                <div className="text-xs leading-5 text-[#7C8AA5]">
                  {f.a}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Support */}
        <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-6">
          <h2 className="font-semibold text-[15px] mb-2">
            Contact Support
          </h2>

          <p className="text-xs text-[#7C8AA5] mb-5">
            Describe your issue and our support team will review your
            request.
          </p>

          {/* Error */}
          {error && (
            <div className="mb-4 rounded-lg border border-[#E5566B]/20 bg-[#E5566B]/10 px-3 py-2.5 text-xs text-[#E5566B]">
              {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#3FBF8F]/20 bg-[#3FBF8F]/10 px-3 py-2.5 text-xs text-[#3FBF8F]">
              <CheckCircle2 size={15} />
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <label className="block text-xs text-[#7C8AA5] mb-2">
              Message
            </label>

            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setError("");
                setSuccess("");
              }}
              rows={6}
              maxLength={2000}
              placeholder="Describe your issue..."
              disabled={loading}
              className="w-full bg-[#161B2C] border border-[#2A3149] rounded-lg px-4 py-3 text-sm mb-2 outline-none focus:border-[#5B8DEF] resize-none disabled:opacity-60"
            />

            <div className="flex justify-between items-center mb-4">
              <span className="text-[11px] text-[#7C8AA5]">
                Minimum 10 characters
              </span>

              <span className="text-[11px] text-[#7C8AA5]">
                {message.length}/2000
              </span>
            </div>

            <button
              type="submit"
              disabled={loading || !message.trim()}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-[#5B8DEF] text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={15} />

              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}